import {defer, from, Observable, ObservableInput, OperatorFunction, Subject, throwError, timer} from 'rxjs';
import {exhaustMap, finalize, mergeMap, retryWhen, throttle} from 'rxjs/operators';

/**
 * From:
 * - https://github.com/ReactiveX/rxjs/issues/1777
 * - https://github.com/ReactiveX/rxjs/issues/5004
 * - https://github.com/bjoerge/rxjs-exhaustmap-with-trailing
 */

/**
 * Like exhaustMap, but also includes the trailing value emitted from the source
 * observable while waiting for the preceding inner observable to complete.
 */
export const exhaustMapWithTrailing =
    <T, R>(project: (value: T, index: number) => ObservableInput<R>): OperatorFunction<T, R> =>
    (source): Observable<R> =>
        defer(() => {
            const release = new Subject<void>();
            return source.pipe(
                throttle(() => release, {
                    leading: true,
                    trailing: true,
                }),
                exhaustMap((value, index) =>
                    from(project(value, index)).pipe(
                        finalize(() => {
                            release.next();
                        }),
                    ),
                ),
            );
        });

export type InferObservable<T> = T extends Observable<infer U> ? U : never;

export type GenericRetryStrategyConfig = {
    maxRetryAttempts?: number;
    scalingDuration?: number;
};

// Taken from https://www.learnrxjs.io/learn-rxjs/operators/error_handling/retrywhen
export const genericRetryStrategy =
    ({maxRetryAttempts = 3, scalingDuration = 1000}: GenericRetryStrategyConfig = {}) =>
    (attempts: Observable<any>) => {
        return attempts.pipe(
            mergeMap((error, i) => {
                const retryAttempt = i + 1;
                // if maximum number of retries have been met throw error
                if (retryAttempt > maxRetryAttempts) {
                    return throwError(error);
                }
                // retry after 1s, 2s, etc...
                return timer(retryAttempt * scalingDuration);
            }),
        );
    };

export const promiseWithRetry = <T extends Promise<any>>(factory: () => T, config?: GenericRetryStrategyConfig) =>
    observableWithRetry$(
        // Use defer to call and create a new promise on each retry
        // Otherwise the old promise will be re-used
        defer(() => from(factory())),
        config,
    ).toPromise();

export const observableWithRetry$ = <T>(importPromise: Observable<T>, config?: GenericRetryStrategyConfig) => {
    return from(importPromise).pipe(retryWhen(genericRetryStrategy(config)));
};
